import {
  WAITING_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
  normalizeRenderConfig,
  sortReportRows,
} from "./renderConfig";
import { buildCsv } from "./agentServiceTimeExport";

const SUMMARY_HEADERS = Object.freeze([
  "Scope",
  "Branch",
  "Service",
  "Completed Tickets",
  "Total Waiting Time",
  "Average",
  "Min",
  "Max",
]);

const DAILY_HEADERS = Object.freeze([
  "Scope",
  "Branch",
  "Service",
  "Day",
  "Completed Tickets",
  "Total Waiting Time",
  "Average",
]);

const toPrimitiveNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const getPointDate = (point) => point.date ?? point.day ?? point.name ?? point.hour ?? "";
const getPoints = (row) => row.days || row.hours || [];
const pointLabelPrefix = (rows) =>
  rows.some((row) => getPoints(row).some((point) => point?.hour !== undefined && point?.hour !== null))
    ? "Hour"
    : "Day";

export const getWaitingTimePerServiceRows = ({ data = [], branchName = "", scope = "" }) =>
  (data || [])
    .filter((service) => toPrimitiveNumber(service?.completed))
    .map((service) => ({
      ...service,
      serviceName: service.name || "",
      branchName,
      scope,
      totalWaitingTime: service.waitingTimeInSeconds,
      average: service.avg,
    }));

export const getWaitingTimePerServiceRowsForScope = (input, renderConfig) => {
  const config = normalizeRenderConfig(renderConfig, {
    columnDefinitions: WAITING_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
  });
  return sortReportRows(getWaitingTimePerServiceRows(input), config, {
    columnDefinitions: WAITING_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
  });
};

export const getWaitingTimePerServiceSummaryCsvRows = (scopes = [], renderConfig) =>
  scopes.flatMap((scope) =>
    getWaitingTimePerServiceRowsForScope(scope, renderConfig).map((row) => [
      row.scope || "",
      row.branchName || "",
      row.serviceName || "",
      toPrimitiveNumber(row.completed),
      toPrimitiveNumber(row.waitingTimeInSeconds),
      toPrimitiveNumber(row.avg),
      toPrimitiveNumber(row.min),
      toPrimitiveNumber(row.max),
    ])
  );

export const getWaitingTimePerServiceDailyCsvRows = (scopes = [], renderConfig) =>
  scopes.flatMap((scope) =>
    getWaitingTimePerServiceRowsForScope(scope, renderConfig).flatMap((row) =>
      getPoints(row).map((point) => [
        row.scope || "",
        row.branchName || "",
        row.serviceName || "",
        getPointDate(point),
        toPrimitiveNumber(point.completed),
        toPrimitiveNumber(point.waitingTimeInSeconds),
        toPrimitiveNumber(point.avg),
      ])
    )
  );

const getDayLabels = (rows) =>
  Array.from(
    new Set(
      rows.flatMap((row) =>
        getPoints(row).map(getPointDate).filter((date) => date !== undefined && date !== null && date !== "")
      )
    )
  );

export const getWaitingTimePerServiceMergedCsvHeaders = (scopes = [], renderConfig) => {
  const rows = scopes.flatMap((scope) => getWaitingTimePerServiceRowsForScope(scope, renderConfig));
  const labels = getDayLabels(rows);
  const prefix = pointLabelPrefix(rows);
  return [...SUMMARY_HEADERS, ...labels.map((day) => `${prefix} ${day}`)];
};

export const getWaitingTimePerServiceMergedCsvRows = (scopes = [], renderConfig) => {
  const rows = scopes.flatMap((scope) => getWaitingTimePerServiceRowsForScope(scope, renderConfig));
  const dayLabels = getDayLabels(rows);
  return rows.map((row) => {
    const valuesByDay = Object.fromEntries(
      getPoints(row).map((point) => [getPointDate(point), toPrimitiveNumber(point.avg)])
    );
    return [
      row.scope || "",
      row.branchName || "",
      row.serviceName || "",
      toPrimitiveNumber(row.completed),
      toPrimitiveNumber(row.waitingTimeInSeconds),
      toPrimitiveNumber(row.avg),
      toPrimitiveNumber(row.min),
      toPrimitiveNumber(row.max),
      ...dayLabels.map((day) => valuesByDay[day] ?? ""),
    ];
  });
};

const triggerCsvDownload = (filename, csv) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

export const downloadWaitingTimePerServiceCsvFiles = ({ scopes, renderConfig, filenamePrefix }) => {
  triggerCsvDownload(
    `${filenamePrefix}.csv`,
    buildCsv(
      getWaitingTimePerServiceMergedCsvHeaders(scopes, renderConfig),
      getWaitingTimePerServiceMergedCsvRows(scopes, renderConfig)
    )
  );
};
